import { Link } from "react-router-dom";
import { ArrowRight, Award, ShieldCheck, Users, Microscope } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/ScrollReveal";

const TEAM = [
    {
        name: "Dr. Martin Dubois",
        role: "Directeur pédagogique · Cursus Thérapie Manuelle",
        photo: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=640&q=80",
        bio: "Kinésithérapeute DU Thérapie Manuelle, 18 ans d'expérience hospitalière et libérale, enseignant à l'Université de Paris.",
    },
    {
        name: "Dr. Isabelle Rochard",
        role: "Directrice pédagogique · Cursus Neurologie & Gériatrie",
        photo: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?auto=format&fit=crop&w=640&q=80",
        bio: "PhD en neurosciences cliniques, kinésithérapeute en unité neuro-vasculaire depuis 15 ans, auteure de travaux sur la plasticité cérébrale.",
    },
    {
        name: "Thomas Bernard",
        role: "Directeur pédagogique · Cursus Rééducation Sportive",
        photo: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=640&q=80",
        bio: "Préparateur physique et kinésithérapeute du sport, 10 ans d'expérience en Top 14 et Ligue 1, consultant en préparation physique.",
    },
    {
        name: "Sophie Laurent",
        role: "Responsable qualité & Qualiopi",
        photo: "https://images.unsplash.com/photo-1580281658626-ee379f3cce93?auto=format&fit=crop&w=640&q=80",
        bio: "Kinésithérapeute, Master en ingénierie de la formation, garante de l'excellence pédagogique et du référentiel qualité Qualiopi.",
    },
];

const VALUES = [
    {
        icon: Microscope,
        title: "Science",
        text: "Nous construisons nos contenus à partir des publications scientifiques les plus récentes et les plus solides.",
    },
    {
        icon: Users,
        title: "Clinique",
        text: "Chaque apport théorique est immédiatement réinvesti dans des ateliers pratiques en petits groupes.",
    },
    {
        icon: Award,
        title: "Excellence",
        text: "Formateurs triés sur le volet, matériel de qualité, lieux soignés : tout concourt à une expérience premium.",
    },
    {
        icon: ShieldCheck,
        title: "Éthique",
        text: "Nous refusons toute complaisance commerciale et assumons notre exigence scientifique et déontologique.",
    },
];

export default function About() {
    return (
        <div data-testid="page-about" className="bg-white">
            {/* Hero */}
            <section className="relative bg-brand-ink text-white pt-40 pb-24 md:pt-48 md:pb-32 grain">
                <div className="mx-auto max-w-7xl px-6 grid gap-12 lg:grid-cols-12 lg:items-center">
                    <Reveal className="lg:col-span-7">
                        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-orange">
                            À propos
                        </span>
                        <h1 className="mt-6 font-display text-5xl font-normal leading-[1.02] tracking-tightest sm:text-6xl lg:text-[88px]">
                            L'Institut de la <em className="italic text-brand-orange">Prévention Active</em>.
                        </h1>
                        <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
                            Cursus Physio est né d'une conviction simple : le kinésithérapeute d'aujourd'hui
                            n'est plus seulement un réparateur, il est un acteur majeur de la prévention en santé.
                            Encore faut-il en maîtriser les outils — scientifiques, cliniques, pédagogiques.
                            C'est ce que nous transmettons, à un niveau d'exigence rare.
                        </p>
                    </Reveal>
                    <Reveal className="lg:col-span-5" delay={0.2}>
                        <div className="overflow-hidden rounded-3xl border border-white/10">
                            <img
                                src="https://images.pexels.com/photos/20860586/pexels-photo-20860586.jpeg"
                                alt="Kiné en consultation"
                                className="aspect-[4/5] w-full object-cover"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Mission */}
            <section className="py-24 md:py-32 bg-white">
                <div className="mx-auto max-w-7xl px-6 grid gap-16 lg:grid-cols-12 lg:gap-20">
                    <Reveal className="lg:col-span-5">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">Notre mission</span>
                        <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-tight">
                            Former à une kinésithérapie <em className="italic text-brand-smoke">proactive</em>.
                        </h2>
                    </Reveal>
                    <Reveal className="lg:col-span-7" delay={0.15}>
                        <p className="text-lg leading-relaxed text-brand-ink/80">
                            Nous voulons offrir aux kinésithérapeutes français ce qu'ils peinent trop souvent
                            à trouver : des formations continues rigoureuses, cliniquement pertinentes, et
                            réellement applicables dès le lundi matin. Chaque cursus est construit autour
                            d'un axe de <strong>Prévention Active</strong> — prévenir la douleur chronique,
                            prévenir la blessure sportive, prévenir la perte d'autonomie.
                        </p>
                        <p className="mt-6 text-lg leading-relaxed text-brand-ink/80">
                            Nous croyons à la responsabilité du thérapeute, à la primauté de la donnée
                            scientifique et à la valeur d'une pratique réflexive. C'est cet esprit qui guide
                            l'ensemble de nos programmes.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Valeurs */}
            <section className="py-24 md:py-32 bg-brand-sand">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-3xl">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">Nos valeurs</span>
                        <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-tight">
                            Quatre principes <em className="italic text-brand-smoke">non négociables</em>.
                        </h2>
                    </Reveal>
                    <StaggerGroup className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                        {VALUES.map((v) => {
                            const Icon = v.icon;
                            return (
                                <StaggerItem key={v.title}>
                                    <div className="h-full rounded-3xl border border-brand-line bg-white p-8 shadow-premium">
                                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        <h3 className="mt-6 font-display text-2xl text-brand-ink">{v.title}</h3>
                                        <p className="mt-3 text-sm leading-relaxed text-brand-ink/75">{v.text}</p>
                                    </div>
                                </StaggerItem>
                            );
                        })}
                    </StaggerGroup>
                </div>
            </section>

            {/* Équipe */}
            <section className="py-24 md:py-32 bg-white">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-3xl">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">L'équipe</span>
                        <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-tight">
                            Celles et ceux qui <em className="italic text-brand-smoke">construisent vos parcours</em>.
                        </h2>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-ink/70 md:text-lg">
                            Des thérapeutes qui <strong className="text-brand-ink">anticipent</strong> et{" "}
                            <strong className="text-brand-ink">diversifient</strong> les approches de demain.
                        </p>
                    </Reveal>
                    <StaggerGroup className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {TEAM.map((m) => (
                            <StaggerItem key={m.name}>
                                <div className="group h-full rounded-3xl border border-brand-line bg-white shadow-premium overflow-hidden transition-all hover:-translate-y-1 hover:shadow-premium-lg">
                                    <div className="aspect-[4/5] w-full overflow-hidden">
                                        <img
                                            src={m.photo}
                                            alt={m.name}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="font-display text-xl text-brand-ink">{m.name}</h3>
                                        <p className="mt-1 text-xs uppercase tracking-wider text-brand-orange">{m.role}</p>
                                        <p className="mt-4 text-sm leading-relaxed text-brand-ink/70">{m.bio}</p>
                                    </div>
                                </div>
                            </StaggerItem>
                        ))}
                    </StaggerGroup>
                </div>
            </section>

            {/* Certifications */}
            <section className="py-24 md:py-32 bg-brand-ink text-white grain">
                <div className="mx-auto max-w-7xl px-6 grid gap-12 lg:grid-cols-12 lg:items-center">
                    <Reveal className="lg:col-span-5">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">
                            Certifications qualité
                        </span>
                        <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-tight">
                            Qualiopi, DPC, <br />
                            <em className="italic text-white/60">une exigence vérifiée</em>.
                        </h2>
                        <p className="mt-8 text-base leading-relaxed text-white/70 max-w-md">
                            Notre organisme est certifié Qualiopi et référencé par l'Agence DPC. Toutes nos
                            actions sont auditées régulièrement pour garantir la qualité de nos formations.
                        </p>
                        <Link
                            to="/prise-en-charge"
                            data-testid="about-cta-prise-en-charge"
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white hover:bg-[#d9772c]"
                        >
                            Voir la prise en charge
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
                                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                    <Award className="h-5 w-5" />
                                </div>
                                <h3 className="mt-6 font-display text-2xl">Qualiopi</h3>
                                <p className="mt-3 text-sm leading-relaxed text-white/70">
                                    Certification qualité des organismes de formation, délivrée par un organisme
                                    accrédité par le COFRAC. Gage d'une démarche qualité exigeante.
                                </p>
                            </div>
                            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
                                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                    <ShieldCheck className="h-5 w-5" />
                                </div>
                                <h3 className="mt-6 font-display text-2xl">Agence DPC</h3>
                                <p className="mt-3 text-sm leading-relaxed text-white/70">
                                    Référencement par l'Agence Nationale du Développement Professionnel
                                    Continu pour les programmes dédiés aux kinésithérapeutes.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
